import React from 'react';
import { motion } from 'framer-motion';

const stats = [
  { value: "2,000+", label: "Vulnerabilities Found" },
  { value: "$4B+",  label: "Assets Secured"       },
  { value: "200+",   label: "Projects Audited"      },
  { value: "15+",    label: "Chains Supported"      },
];

/* Stripe-style flowing ribbon, right side. Pure SVG + CSS transforms, no JS. */
const Ribbon = () => (
  <div className="hero-ribbon" aria-hidden="true">
    <svg viewBox="0 0 1000 900" preserveAspectRatio="xMaxYMid slice">
      <defs>
        <linearGradient id="hero-rb-1" x1="0" y1="1" x2="1" y2="0">
          <stop offset="0%" stopColor="#00A99E" />
          <stop offset="45%" stopColor="#22D3EE" />
          <stop offset="80%" stopColor="#6366F1" />
          <stop offset="100%" stopColor="#C084FC" />
        </linearGradient>
        <linearGradient id="hero-rb-2" x1="0" y1="1" x2="1" y2="0">
          <stop offset="0%" stopColor="#5EF0E4" />
          <stop offset="55%" stopColor="#0891B2" />
          <stop offset="100%" stopColor="#818CF8" />
        </linearGradient>
        <linearGradient id="hero-rb-3" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#A5F3FC" />
          <stop offset="100%" stopColor="#00A99E" />
        </linearGradient>
        <filter id="hero-rb-soft" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="22" />
        </filter>
      </defs>

      {/* Wide band with a soft glow underneath */}
      <g className="hero-ribbon-band">
        <path
          d="M160 1000 C 430 900, 470 600, 660 470 S 930 330, 1060 -60"
          fill="none" stroke="url(#hero-rb-1)" strokeWidth="240" strokeLinecap="round"
          filter="url(#hero-rb-soft)" opacity="0.55"
        />
        <path
          d="M160 1000 C 430 900, 470 600, 660 470 S 930 330, 1060 -60"
          fill="none" stroke="url(#hero-rb-1)" strokeWidth="170" strokeLinecap="round"
        />
      </g>
      {/* Second strand crossing over */}
      <g className="hero-ribbon-band hero-ribbon-band--2">
        <path
          d="M420 1040 C 560 860, 700 760, 800 560 S 900 220, 1180 60"
          fill="none" stroke="url(#hero-rb-2)" strokeWidth="84" strokeLinecap="round" opacity="0.9"
        />
      </g>
      {/* Thin highlight thread */}
      <g className="hero-ribbon-band hero-ribbon-band--3">
        <path
          d="M60 900 C 330 820, 400 540, 590 420 S 860 250, 980 -80"
          fill="none" stroke="url(#hero-rb-3)" strokeWidth="26" strokeLinecap="round" opacity="0.85"
        />
      </g>
    </svg>
  </div>
);

const Chevron = () => (
  <svg className="hero-chev" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 5l7 7-7 7" />
  </svg>
);

const HomeHero = () => (
  <section className="relative bg-black overflow-hidden">
    <Ribbon />

    {/* ── Content ── */}
    <div className="relative z-10 mx-auto w-full max-w-6xl px-6 pt-20 md:pt-28">

      {/* Trust badge */}
      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="mb-7 inline-flex items-center gap-2.5 text-sm font-semibold text-teal-300"
      >
        <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#00A99E] shadow-[0_0_0_4px_rgba(0,169,158,0.18)]" aria-hidden="true" />
        Trusted by 200+ Projects Globally
      </motion.p>

      {/* Headline — no opacity:0 initial to avoid blocking LCP */}
      <h1 className="mb-7 text-[2.75rem] font-semibold leading-[1.02] tracking-[-0.04em] sm:text-6xl lg:text-[84px]">
        <span className="text-white">Revolutionizing</span>
        <br />
        <span
          className="text-transparent bg-clip-text"
          style={{ backgroundImage: 'linear-gradient(90deg, #00A99E 0%, #5ef0e4 55%, #a5b4fc 100%)' }}
        >
          Blockchain Audits
        </span>
      </h1>

      {/* Description */}
      <motion.p
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="mb-10 max-w-[36rem] text-lg leading-relaxed text-[#a3acb9] md:text-xl"
      >
        <span className="font-semibold text-[#e6ebf1]">Safe Edges</span> is a world-renowned smart contract audit firm, securing over $4B+ in digital assets. Official security partner of Fuel Chain,
         Clique, and 200+ protocols globally. Not a single client has ever been hacked.

      </motion.p>

      {/* CTAs */}
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, delay: 0.42 }}
        className="flex flex-wrap items-center gap-3"
      >
        <a
          href="/contact"
          target="_blank"
          rel="noopener noreferrer"
          className="hero-btn inline-flex items-center gap-1.5 rounded-full bg-[#00A99E] px-5 py-3 text-[15px] font-semibold text-black transition-colors duration-200 hover:bg-[#2ee6d6]"
        >
          Request Audit
          <Chevron />
        </a>
        <a
          href="/services"
          className="hero-btn inline-flex items-center gap-1.5 rounded-full px-5 py-3 text-[15px] font-semibold text-[#e6ebf1] ring-1 ring-white/15 transition-colors duration-200 hover:bg-white/[0.06] hover:ring-white/30"
        >
          Explore Services
          <Chevron />
        </a>
      </motion.div>

      {/* Stats — a quiet row under a hairline, like Stripe's logo strip */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.65, delay: 0.55 }}
        className="relative z-10 mt-20 grid grid-cols-2 border-t border-white/[0.08] sm:grid-cols-4 md:mt-28"
      >
        {stats.map((s, i) => (
          <div key={i} className={`py-8 pr-6 ${i < 2 ? 'border-b border-white/[0.08] sm:border-b-0' : ''}`}>
            <p className="text-3xl font-semibold tracking-[-0.03em] text-white tabular-nums lg:text-[40px] lg:leading-none">{s.value}</p>
            <p className="mt-3 text-sm text-[#8792a2]">{s.label}</p>
          </div>
        ))}
      </motion.div>
    </div>

    <style>{`
      .hero-ribbon {
        position: absolute;
        z-index: 0;
        top: -120px;
        bottom: 170px; /* stop above the stats row */
        right: -6%;
        width: min(980px, 72vw);
        pointer-events: none;
        opacity: 0.8;
        /* fade in from the left (behind the copy) and out toward the stats row */
        -webkit-mask-image: linear-gradient(100deg, transparent 6%, #000 40%), linear-gradient(to bottom, #000 62%, transparent 100%);
        -webkit-mask-composite: source-in;
        mask-image: linear-gradient(100deg, transparent 6%, #000 40%), linear-gradient(to bottom, #000 62%, transparent 100%);
        mask-composite: intersect;
      }
      .hero-ribbon svg { width: 100%; height: 100%; overflow: visible; }
      .hero-ribbon-band { transform-origin: 60% 40%; animation: heroRibbonDrift 18s ease-in-out infinite alternate; }
      .hero-ribbon-band--2 { animation-duration: 23s; animation-direction: alternate-reverse; }
      .hero-ribbon-band--3 { animation-duration: 29s; }
      @keyframes heroRibbonDrift {
        from { transform: translate3d(0, 0, 0) rotate(0deg); }
        to   { transform: translate3d(-28px, 22px, 0) rotate(2.5deg); }
      }
      @media (max-width: 1023px) {
        .hero-ribbon { right: -40%; width: 110vw; bottom: 45%; opacity: 0.4; }
      }

      .hero-chev { transition: transform 0.2s ease; }
      .hero-btn:hover .hero-chev { transform: translateX(3px); }

      @media (prefers-reduced-motion: reduce) {
        .hero-ribbon-band { animation: none; }
      }
    `}</style>
  </section>
);

export default HomeHero;
